import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import * as Immutable from 'immutable';
import { CustomShowcaseCreatedFrom, CustomShowcaseSortOrder } from '../../custom-showcase/models/custom-showcase';
import { Entity, FormatGroup } from '../../entity/models/entity';
import { ListPagination } from '../../list/models/list.dto';
import * as LibraryListActions from '../actions/library-list.actions';

export enum LibraryListTypes {
  LIST = 'List',
  SHOWCASE = 'Showcase',
}

export interface LibraryList {
  id: string;
  name: string;
  type: LibraryListTypes;
  creator?: string;
  createdFrom?: CustomShowcaseCreatedFrom;
  sortBy?: CustomShowcaseSortOrder;
}

export interface LoadingState {
  loading: boolean;
  errorCode: number | null;
  loaded?: boolean;
}

export const LibraryListLoadingStateRecord = Immutable.Record<LoadingState>({
  loading: false,
  errorCode: null,
  loaded: false,
});

export const LibraryListItemsLoadingStateRecord = Immutable.Record<LoadingState>({
  loading: false,
  errorCode: null,
});

export const LibraryListItemsPaginationRecord = Immutable.Record<ListPagination>({
  page: 0,
  totalPages: 0,
  totalResults: 0,
});

export interface LibraryListState {
  libraryList: LibraryList;
  libraryListLoadingState: Immutable.Record<LoadingState>,
  items: Immutable.List<(Entity | FormatGroup)>;
  itemsLoadingState: Immutable.Record<LoadingState>;
  itemsPagination: Immutable.Record<ListPagination>;
}

export const initialState: LibraryListState = {
  libraryList: null,
  libraryListLoadingState: LibraryListLoadingStateRecord({loading: false}),
  items: Immutable.List(),
  itemsLoadingState: LibraryListItemsLoadingStateRecord(),
  itemsPagination: LibraryListItemsPaginationRecord(),
};

export const isLastPageLoaded = (pagination: ListPagination) => (pagination.page >= pagination.totalPages - 1);

const _reducer = createReducer(
  initialState,
  on(LibraryListActions.loadLibraryList, (state) => ({
    ...state,
    libraryListLoadingState: LibraryListLoadingStateRecord({loading: true}),
  })),
  on(LibraryListActions.loadLibraryListSuccess, (state, {libraryList}) => ({
    ...state,
    libraryListLoadingState: LibraryListLoadingStateRecord(),
    libraryList,
  })),
  on(LibraryListActions.loadLibraryListFailure, (state, {errorCode}) => ({
    ...state,
    libraryListLoadingState: LibraryListLoadingStateRecord({errorCode}),
  })),
  on(LibraryListActions.loadLibraryListItems, (state) => ({
    ...state,
    itemsLoadingState: LibraryListItemsLoadingStateRecord({loading: true}),
  })),
  on(LibraryListActions.loadLibraryListItemsSuccess, (state, {pagination}) => ({
    ...state,
    itemsLoadingState: LibraryListItemsLoadingStateRecord(),
    itemsPagination: LibraryListItemsPaginationRecord(pagination),
  })),
  on(LibraryListActions.loadLibraryListItemsFailure, (state, {errorCode}) => ({
    ...state,
    itemsLoadingState: LibraryListItemsLoadingStateRecord({errorCode}),
  })),
  on(LibraryListActions.addLoadedLibraryListItems, (state, {entities}) => ({
    ...state,
    items: state.items.push(...entities),
  })),
  on(LibraryListActions.loadMoreLibraryListItems, (state) => ({
    ...state,
    itemsLoadingState: LibraryListItemsLoadingStateRecord({loading: true}),
  })),
  on(LibraryListActions.clearLibraryListItems, (state) => ({
    ...state,
    items: state.items.clear(),
  })),
);

export function reducer(state: LibraryListState | undefined, action: Action) {
  return _reducer(state, action);
}

export const featureKey = 'libraryList';

export const getLibraryListState = createFeatureSelector<LibraryListState>(featureKey);

export const getLibraryList = createSelector(getLibraryListState, (state: LibraryListState): LibraryList => {
  return state.libraryList;
});

export const getLibraryListLoadingState = createSelector(getLibraryListState, (state: LibraryListState): LoadingState => {
  return state.libraryListLoadingState.toJS();
});

export const getLibraryListItems = createSelector(getLibraryListState, (state: LibraryListState): (Entity | FormatGroup)[] => {
  return state.items.toJS() as (Entity | FormatGroup)[];
});

export const getItemsLoadingState = createSelector(getLibraryListState, (state: LibraryListState): LoadingState => {
  return state.itemsLoadingState.toJS();
});

export const getItemsPagination = createSelector(getLibraryListState, (state: LibraryListState): ListPagination => {
  return state.itemsPagination.toJS();
});
